import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  output,
  input,
} from '@angular/core';
import { AnalyticsService } from '@geneplanet/ngx-utils/src/lib/analytics';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { HealthscoreTabs } from 'src/app/healthscore/constants/healthscore-tabs';
import { MeService } from 'src/app/me/services/me.service';
import { FeatureFlagGuard } from 'src/app/shared/guards/feature.guard';
import { CustomerCheckService } from 'src/app/shared/services/customer-check.service';
import { environment } from 'src/environments/environment';
import { Category } from '../../interfaces';
import { SideMenuService } from '../../services/side-menu.service';
import { LanguageService } from 'src/app/shared/services/language.service';
import { isFeatureActive } from 'src/app/shared/util/feature-active';
import { LokaliseTranslatePipe } from '../../../lokalise/lokalise-translate.pipe';
import { CardNavItemComponent } from '../../../shared/components/card-nav-item/card-nav-item.component';
import { NgClass } from '@angular/common';

interface ResultLink {
  url: string;
  title: string;
  icon: string;
  visible: boolean;
  fragment?: string;
  dataTrack: string;
  new?: boolean;
  newAnalyses?: boolean;
}

function trueIfUndefined(value: boolean): boolean {
  return value === undefined ? true : value;
}

@UntilDestroy()
@Component({
  selector: 'app-results-menu',
  templateUrl: './results-menu.component.html',
  styleUrls: ['./results-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, CardNavItemComponent, LokaliseTranslatePipe],
  host: { '(window:resize)': 'onResize($event)' },
})
export class ResultsMenuComponent implements OnInit {
  private meService = inject(MeService);
  private cdRef = inject(ChangeDetectorRef);
  private sideMenuService = inject(SideMenuService);
  private customerCheckService = inject(CustomerCheckService);
  private modals = inject(NgbModal);
  private analyticsService = inject(AnalyticsService);
  private languageService = inject(LanguageService);

  readonly mobile = input(true, { transform: trueIfUndefined });
  readonly collapsed = input(false);
  readonly navItemClicked = output();

  categories: Category[] | undefined;

  dashboardLink!: ResultLink;
  dnaLinks: ResultLink[] = [];
  lifestyleLinks: ResultLink[] = [];
  featureLinks: ResultLink[] = [];

  onResize(event: UIEvent) {
    if ((event.target as Window).innerWidth < 768) {
      this.dashboardLink.visible = false;
    } else {
      this.dashboardLink.visible = true;
    }
  }

  ngOnInit() {
    this.updateLinks();
    this.meService.isNiptUser
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(() => this.updateLinks());

    this.languageService.currentLocation$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(() => this.updateLinks());

    this.sideMenuService.categories$
      .pipe(untilDestroyed(this))
      .subscribe((categories) => {
        this.categories = categories;
        this.updateLinks();
      });

    this.customerCheckService.customerExists$
      .pipe(
        filter((exists) => exists),
        switchMap(() => this.sideMenuService.fetchCategories()),
        untilDestroyed(this)
      )
      .subscribe((categories) => {
        this.sideMenuService.categories$.next(categories);
      });

    this.meService.getFullProfile()?.subscribe();
  }

  navItemClick(data: string) {
    this.analyticsService.trackClicksManually(data);
    this.modals.dismissAll();
    this.navItemClicked.emit();
  }

  private updateLinks() {
    const { myLifestyle, myHealth, myAncestry, dnaTestResults } =
      environment.categories;

    this.dashboardLink = {
      url: '/dashboard',
      title: 'DASHBOARD.TITLE',
      icon: 'gpicon-dashboard',
      visible: !this.mobile(),
      dataTrack: 'results-dashboard',
    };

    this.dnaLinks = [
      {
        url: '/dna',
        title: 'RESULTS.MENU.ALL.LIFESTYLE',
        icon: 'gpicon-sports-and-recreation',
        visible: this.categoryExists(myLifestyle),
        dataTrack: 'results-mylifestyle',
      },
      {
        url: '/prs',
        title: 'RESULTS.MENU.ALL.HEALTH',
        icon: 'gpicon-immune-system',
        visible: this.categoryExists(myHealth),
        dataTrack: 'results-myhealth',
        newAnalyses: !isFeatureActive(
          this.languageService.locationCode,
          'newPrs'
        ),
      },
      {
        url: '/ancestry',
        title: 'RESULTS.MENU.ALL.ANCESTRY',
        icon: 'gpicon-ancestry',
        visible:
          this.categoryExists(myAncestry) &&
          FeatureFlagGuard.isFeatureActive('ancestry'),
        dataTrack: 'results-ancestry',
      },
      {
        url: '/dna-snp',
        title: 'RESULTS.MENU.ALL.DNA',
        icon: 'gpicon-dna',
        visible: this.categoryExists(dnaTestResults),
        dataTrack: 'results-dna-snp',
      },
      {
        url: '/nipt',
        title: 'RESULTS.MENU.ALL.NIPT',
        icon: 'gpicon-nipt',
        visible:
          FeatureFlagGuard.isFeatureActive('nipt') &&
          this.meService.isNiptUser.value,
        dataTrack: 'results-nipt',
      },
    ];

    this.lifestyleLinks = [
      {
        url: '/healthscore',
        title: 'RESULTS.MENU.ALL.HEALTHSCORE',
        icon: 'gpicon-healthscore',
        fragment: HealthscoreTabs.DISCOVER,
        visible: true,
        dataTrack: 'results-healthscore',
      },
      {
        url: '/measurement',
        title: 'RESULTS.MENU.ALL.BODY',
        icon: 'gpicon-body',
        visible: true,
        dataTrack: 'results-measurements',
      },
      {
        url: '/blood',
        title: 'RESULTS.MENU.ALL.BLOOD',
        icon: 'gpicon-blood',
        visible: true,
        dataTrack: 'results-blood',
      },
      {
        url: '/habits',
        title: 'RESULTS.MENU.ALL.HABITS',
        icon: 'gpicon-habits',
        visible: true,
        dataTrack: 'results-habits',
      },
      {
        url: '/microbiome',
        title: 'RESULTS.MENU.ALL.MICROBIOME',
        icon: 'gpicon-oral-microbiome',
        visible: FeatureFlagGuard.isFeatureActive('oralMicrobiome'),
        dataTrack: 'results-microbiome',
      },
    ];

    this.featureLinks = [
      {
        url: '/meal-analyser',
        title: 'RESULTS.MENU.ALL.MEAL_ANALYSER',
        icon: 'gpicon-meal',
        visible: isFeatureActive(
          this.languageService.locationCode,
          'mealAnalyser'
        ),
        new: true,
        dataTrack: 'results-meal-analyser',
      },
      {
        url: '/recipes',
        title: 'RESULTS.MENU.ALL.RECIPES',
        icon: 'gpicon-recipe-generator',
        // Thailand should not see recipe generator
        visible: isFeatureActive(
          this.languageService.locationCode,
          'recipeGenerator'
        ),
        new: true,
        dataTrack: 'results-recipe-generator',
      },
      {
        url: '/foodid',
        title: 'RESULTS.MENU.ALL.FOODID',
        icon: 'gpicon-food',
        visible: FeatureFlagGuard.isFeatureActive('foodid'),
        dataTrack: 'results-foodid',
      },
    ];
    this.cdRef.markForCheck();
  }

  private categoryExists(categoryId: string): boolean {
    return !!this.categories?.find((c) => c.categoryId === categoryId);
  }
}
